import { fetchFieldAppTracking } from "./fieldAppApi";
import {
  dayKeysNewestFirst,
  selectionForDayKey,
} from "./fieldAppPeriodUtils";
import {
  buildTrackingCacheKey,
  getCachedTracking,
  setCachedTracking,
} from "./fieldAppTrackingCache";
import {
  hasDisplayableTracking,
  mergeTrackingPayloads,
} from "./fieldAppTrackingMerge";

function waitForMapPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== "function") {
      setTimeout(resolve, 32);
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

async function fetchTrackingDay(farmId, selection, dayKey) {
  const daySelection = selectionForDayKey(selection, dayKey);
  const cacheKey = buildTrackingCacheKey(farmId, daySelection);
  const cached = getCachedTracking(cacheKey);
  if (cached) return cached;

  const data = await fetchFieldAppTracking(farmId, daySelection);
  if (data) {
    setCachedTracking(cacheKey, data);
  }
  return data;
}

/**
 * O primeiro dia com pontos (o mais recente) permanece na tela.
 * Os dias seguintes não redesenham o gráfico. O período inteiro entra
 * só quando o carregamento termina.
 */
export function publishedTrackingSnapshot(
  current,
  partial,
  { complete = false, hasDisplayable = false } = {}
) {
  if (complete) return partial || current;
  if (current) return current;
  if (hasDisplayable && partial) return partial;
  return current;
}

export async function fetchTrackingProgressive(
  farmId,
  selection,
  { onProgress, isCancelled } = {}
) {
  const dayKeys = dayKeysNewestFirst(selection.dateFrom, selection.dateTo);
  if (!dayKeys.length) {
    return null;
  }

  if (dayKeys.length === 1) {
    const data = await fetchTrackingDay(farmId, selection, dayKeys[0]);
    if (onProgress) {
      onProgress({
        tracking: data,
        loadedDays: 1,
        totalDays: 1,
        dayKey: dayKeys[0],
        complete: true,
      });
    }
    return data;
  }

  const partials = [];
  let loadedDays = 0;
  let announcedMap = false;

  for (const dayKey of dayKeys) {
    if (isCancelled && isCancelled()) {
      return mergeTrackingPayloads(partials);
    }

    let dayData = null;
    try {
      dayData = await fetchTrackingDay(farmId, selection, dayKey);
    } catch (error) {
      console.error(`Falha ao carregar rastreamento do dia ${dayKey}`, error);
    }

    loadedDays += 1;
    if (dayData) {
      partials.push(dayData);
    }

    const tracking = mergeTrackingPayloads(partials);
    const complete = loadedDays >= dayKeys.length;

    if (onProgress) {
      onProgress({
        tracking,
        loadedDays,
        totalDays: dayKeys.length,
        dayKey,
        complete,
      });
    }

    // Deixa o React pintar o mapa deste dia antes de buscar o próximo.
    // Sem esse respiro, os dias em cache resolvem no mesmo ciclo e a
    // imagem de satélite só aparece quando a semana inteira termina.
    if (
      !announcedMap &&
      !complete &&
      hasDisplayableTracking(tracking)
    ) {
      announcedMap = true;
      await waitForMapPaint();
      if (isCancelled && isCancelled()) {
        return mergeTrackingPayloads(partials);
      }
    }
  }

  return mergeTrackingPayloads(partials);
}

export { hasDisplayableTracking };
